import { useEffect, useState } from "react";
import type { GameTileConfig } from "@hunch/core";
import { fetchGameTiles } from "../lib/api.js";
import { GAME_REGISTRY } from "../games/registry.js";
import { GameTile } from "../components/GameTile.js";
import { HubScene } from "../components/illustrations/HubScene.js";
import { useI18n } from "../i18n/index.js";
import styles from "./Hub.module.css";

type LoadState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; tiles: GameTileConfig[] };

export function Hub() {
  const { lang, t } = useI18n();
  const [state, setState] = useState<LoadState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    fetchGameTiles()
      .then((tiles) => {
        if (!cancelled) setState({ status: "ready", tiles });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className={styles.main}>
      <section className={styles.hero}>
        <HubScene className={styles.heroArt} />
        <p className={styles.kicker}>{t("app.suiteKicker")}</p>
        <h1 className={styles.title}>{t("app.suiteName")}</h1>
        <p className={styles.tagline}>{t("app.suiteTagline")}</p>
      </section>

      {state.status === "loading" && <p className={styles.status}>{t("hub.loading")}</p>}
      {state.status === "error" && <p className={styles.statusError}>{t("hub.loadError")}</p>}

      {state.status === "ready" && (
        <div className={styles.grid}>
          {state.tiles.map((tile) => {
            const module = GAME_REGISTRY[tile.gameId];
            return (
              <GameTile
                key={tile.gameId}
                gameId={tile.gameId}
                route={module.route}
                name={module.name}
                tagline={module.tagline[lang]}
                info={module.roundsInfo[lang]}
              />
            );
          })}
        </div>
      )}
    </main>
  );
}
